import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import adminAPI from '../utils/api';

const ClientServiceEditModal = ({ isOpen, onClose, onSuccess, clientService }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    total_sessions: 0,
    used_sessions: 0,
    current_month_sessions: 0,
    current_month_used: 0,
    status: 'active'
  });

  // Initialize form data when clientService changes
  useEffect(() => {
    if (clientService) {
      setFormData({
        start_date: clientService.start_date || '',
        end_date: clientService.end_date || '',
        total_sessions: clientService.total_sessions || 0,
        used_sessions: clientService.used_sessions || 0,
        current_month_sessions: clientService.current_month_sessions || 0,
        current_month_used: clientService.current_month_used || 0,
        status: clientService.status || 'active'
      });
    }
  }, [clientService]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validation
      if (formData.used_sessions > formData.total_sessions) {
        toast.error('Les séances utilisées ne peuvent pas dépasser le total des séances');
        return;
      }

      if (formData.current_month_used > formData.current_month_sessions) {
        toast.error('Les séances utilisées ce mois ne peuvent pas dépasser le total mensuel');
        return;
      }

      if (new Date(formData.end_date) < new Date(formData.start_date)) {
        toast.error('La date de fin doit être postérieure à la date de début');
        return;
      }

      await adminAPI.updateClientService(clientService.id, formData);
      toast.success('Service client mis à jour avec succès');
      onSuccess();
    } catch (error) {
      console.error('Error updating client service:', error);
      toast.error(error.message || 'Erreur lors de la mise à jour du service');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Modifier le service client
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Client Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Informations du client</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Client</label>
                <p className="text-sm text-gray-900">{clientService?.client?.full_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Service</label>
                <p className="text-sm text-gray-900">{clientService?.service_type?.name}</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de début *
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de fin *
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* Sessions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total des séances *
                </label>
                <input
                  type="number"
                  name="total_sessions"
                  value={formData.total_sessions}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Séances utilisées *
                </label>
                <input
                  type="number"
                  name="used_sessions"
                  value={formData.used_sessions}
                  onChange={handleInputChange}
                  min="0"
                  max={formData.total_sessions}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* Monthly sessions (for abonnements) */}
            {clientService?.service_type?.type === 'abonnement' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Séances mensuelles
                  </label>
                  <input
                    type="number"
                    name="current_month_sessions"
                    value={formData.current_month_sessions}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Séances utilisées ce mois
                  </label>
                  <input
                    type="number"
                    name="current_month_used"
                    value={formData.current_month_used}
                    onChange={handleInputChange}
                    min="0"
                    max={formData.current_month_sessions}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Statut *
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              >
                <option value="active">Actif</option>
                <option value="pending">En attente</option>
                <option value="expired">Expiré</option>
                <option value="cancelled">Annulé</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Mise à jour...' : 'Mettre à jour'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClientServiceEditModal;
